import { useState, useCallback } from 'react'
import { FormattedMessage, FormattedDate, useIntl } from 'react-intl'
import { useSnackbar } from 'notistack'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Add from '@mui/icons-material/Add'
import Edit from '@mui/icons-material/Edit'
import Delete from '@mui/icons-material/Delete'
import { isEmpty } from 'lodash'
import PageSection from '../../../../shared/components/layouts/PageSection'
import FormDrawer from '../../../../shared/components/drawers/FormDrawer'
import NetworkForm from '../../networks/components/NetworkForm'
import { useConfirmationDialog } from '../../../../system/ConfirmationDialogProvider'

const getDriverColor = (driver) => {
  switch (driver) {
    case 'bridge':
      return 'default'
    case 'overlay':
      return 'primary'
    default:
      return 'default'
  }
}

function ProjectNetworksTab({
  networks,
  loading,
  createNetwork,
  updateNetwork,
  destroyNetwork,
  onSuccess
}) {
  const { formatMessage } = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const { openConfirmationDialog } = useConfirmationDialog()
  const [drawer, setDrawer] = useState({ open: false, network: null })

  const handleOpenDrawer = useCallback((network = null) => {
    setDrawer({ open: true, network })
  }, [])

  const handleCloseDrawer = useCallback(() => {
    setDrawer({ open: false, network: null })
  }, [])

  const handleSubmit = useCallback((values) => {
    const networkData = { network: values }

    if (drawer.network) {
      return updateNetwork(drawer.network.id, networkData)
        .then(() => {
          handleCloseDrawer()
          onSuccess()
        })
    }

    return createNetwork(networkData)
      .then(() => {
        handleCloseDrawer()
        onSuccess()
      })
  }, [drawer.network, createNetwork, updateNetwork, handleCloseDrawer, onSuccess])

  const handleDelete = useCallback((network) => {
    openConfirmationDialog({
      title: <FormattedMessage id="networks.delete.title" />,
      message: <FormattedMessage id="networks.delete.message" values={{ name: network.name }} />,
      onConfirm: () => destroyNetwork(network.id)
        .then(() => {
          onSuccess()
        })
        .catch(() => {
          enqueueSnackbar(formatMessage({ id: 'networks.delete.error' }), { variant: 'error' })
        }),
      onCancel: () => Promise.resolve(),
    })
  }, [openConfirmationDialog, destroyNetwork, onSuccess, enqueueSnackbar, formatMessage])

  return (
    <>
      <PageSection titleId="dockerProjects.sections.networks" loading={loading}>
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDrawer()}
          >
            <FormattedMessage id="networks.create.title" />
          </Button>
        </Box>

        {isEmpty(networks) ? (
          <Typography color="text.secondary">
            <FormattedMessage id="networks.empty" />
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <FormattedMessage id="networks.index.columns.name" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="networks.index.columns.dockerName" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="networks.index.columns.driver" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="networks.index.columns.description" />
                  </TableCell>
                  <TableCell>
                    <FormattedMessage id="networks.index.columns.createdAt" />
                  </TableCell>
                  <TableCell align="right">
                    <FormattedMessage id="networks.index.columns.actions" />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {networks.map((network) => (
                  <TableRow key={network.id}>
                    <TableCell>{network.name}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {network.dockerName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={network.driver}
                        color={getDriverColor(network.driver)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{network.description || '-'}</TableCell>
                    <TableCell>
                      {network.createdAt ? (
                        <FormattedDate
                          value={network.createdAt}
                          year="numeric"
                          month="short"
                          day="numeric"
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDrawer(network)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(network)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </PageSection>

      <FormDrawer
        open={drawer.open}
        onClose={handleCloseDrawer}
        titleId={drawer.network ? 'networks.edit.title' : 'networks.create.title'}
      >
        <NetworkForm
          network={drawer.network}
          onSubmit={handleSubmit}
          onCancel={handleCloseDrawer}
        />
      </FormDrawer>
    </>
  )
}

export default ProjectNetworksTab
